// REST layer. Mirrors the Flutter app exactly: plain JSON POSTs to the same
// backend (no auth header — the server's encryptedAuth middleware is pass-through,
// just like the Flutter client relies on).

import axios from 'axios';

export const API_BASE =
  process.env.REACT_APP_API_URL || 'http://smartifyindia.co.in:8080/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

const post = (endpoint, body = {}) => api.post(`/${endpoint}`, body);

// ---------------- Auth / user ----------------
export const login = (mobileNumber, password) =>
  post('GetUserDetailsbyMobileNumberAndPassword', {
    MobileNumber: mobileNumber,
    Password: password,
  });

export const sendOtp = (mobileNumber) =>
  post('SendOtpOnWhatsAppbyMobileNumber', {
    MobileNumber: mobileNumber,
    ServiceBy: 'smartifyindia',
  });

export const registerUser = (payload) => post('AddNewUser', payload);

export const updateUser = (data, where) =>
  post('UpdateUsers', { data, where });

// ---------------- Companies (Super Admin) ----------------
export const getCompanies = () => post('GetDataFromCompanyInfo', {});
export const addCompany = (payload) => post('AddCompanyInfo', payload);
export const updateCompany = (data, where) =>
  post('UpdateCompanyInfo', { data, where });

// ---------------- Device Types (Super Admin catalog) ----------------
// The product/device-type catalog (homeautomation.DeviceType). The list query
// joins ImageData + OTABinFile, so a row carries more columns than the table —
// send ONLY DeviceType columns back on add/update (see DEVICE_TYPE_COLUMNS).
export const getDeviceTypes = () => post('GetAllDeviceTypeDetails', {});

// AddDeviceType upserts on DeviceName (body IS the row).
export const addDeviceType = (row) => post('AddDeviceType', row);

// UpdateDeviceType takes { data, where } like every other Update endpoint.
export const updateDeviceType = (data, where) =>
  post('UpdateDeviceType', { data, where });

// ---------------- Locations ----------------
export const getLocations = (userId) =>
  post('GetLocationsByUserId', { UserId: userId });

export const getLocationDetail = (locationId) =>
  post('GetLocationDetailsbyLocationID', { LocationID: locationId });

// ---------------- Rooms ----------------
export const getRooms = (userId, locationId) =>
  post('GetRoomsbyUserIDandLocationId', { UserId: userId, LocationID: locationId });

export const getCommonDevices = (userId, locationId) =>
  post('GetCommonDevicesbyUserIDandLocationId', {
    UserId: userId,
    LocationID: locationId,
  });

export const getDevicesByRoom = (userId, roomId) =>
  post('GetDeviceDetailsbyUserIdAndRoomId', { UserId: userId, RoomID: roomId });

// ---------------- Reorder (SortOrder) ----------------
// Locations + devices are per-user (mapping tables); rooms are global (Rooms table).
export const updateLocationSortOrder = (userId, locationId, sortOrder) =>
  post('UpdateUserLocationMappingbyUserIdAndLocationID', {
    data: { SortOrder: sortOrder },
    where: `UserId = ${userId} AND LocationID = ${locationId}`,
  });

export const updateRoomSortOrder = (roomId, sortOrder) =>
  post('UpdateRooms', {
    data: { SortOrder: sortOrder },
    where: `RoomID = ${roomId}`,
  });

export const updateDeviceSortOrder = (userId, deviceInstalledId, sortOrder) =>
  post('UpdateUserDeviceMappingByUserIDAndDeviceInstalledID', {
    data: { SortOrder: sortOrder },
    where: `UserId = ${userId} AND DeviceInstalledID = ${deviceInstalledId}`,
  });

// ---------------- Devices ----------------
export const getDevicesByUser = (userId) =>
  post('GetListofDevicesbyUserId', { UserId: userId });

export const getFavouriteDevices = (userId) =>
  post('GetListoffavouriteDevicesbyUserId', { UserId: userId });

export const getDeviceDetail = (uniqueNumber) =>
  post('GetDeviceDetailbyUniqueNumber', { UniqueNumber: uniqueNumber });

export const getDeviceStatus = (uniqueNumber) =>
  post('GetDeviceStatusbyUniqueNumber', { UniqueNumber: uniqueNumber });

export const getOutputs = (uniqueNumber) =>
  post('GetOutputDetailbyUniqueNumber', { UniqueNumber: uniqueNumber });

// Current-sense readings for a phase meter on a given day (YYYY-M-D).
// Returns rows { DateTime, current, power, phase } (~every 30-60s).
export const getCurrentSense = (uniqueNumber, dateStr) =>
  post('GetCurrentSenseByUniqueNumberAndDateOnly', {
    UniqueNumber: uniqueNumber,
    DateTime: dateStr,
  });

export const getButtonsByLocationAndUnique = (locationId, uniqueNumber) =>
  post('GetButtonsbyLocationIdAndUniqueNumber', {
    LocationID: locationId,
    UniqueNumber: uniqueNumber,
  });

export const updateOutputName = (outputName, uniqueNumber, output) =>
  post('UpdateOutputDetails', {
    data: { OutputName: outputName },
    where: `UniqueNumber = '${uniqueNumber}' AND Output = '${output}'`,
  });

// ---------------- Device settings (rename, output props) ----------------
export const updateDeviceName = (uniqueNumber, deviceName) =>
  post('UpdateDeviceInstalledByUniqueNumber', {
    data: { DeviceName: deviceName },
    UniqueNumber: uniqueNumber,
  });

// Generic per-output update ({ OutputName } now; button props later).
export const updateOutputDetail = (uniqueNumber, output, data) =>
  post('UpdateOutputDetailsByUniqueNumberAndOutput', {
    data,
    UniqueNumber: uniqueNumber,
    Output: output,
  });

export const setFavourite = (deviceInstalledId, userId, favourite) =>
  post('UpdateUserDeviceMapping', {
    data: { favourite: favourite ? 1 : 0 },
    where: `DeviceInstalledID = ${deviceInstalledId} AND UserId = ${userId}`,
  });

// ---------------- Schedules ----------------
export const getSchedules = (uniqueNumber, output) =>
  post('GetSchedulerListByUniqueNumberAndOutput', {
    UniqueNumber: uniqueNumber,
    Output: output,
  });

export const addSchedule = (payload) => post('AddSchedulerList', payload);
export const updateSchedule = (data, where) =>
  post('UpdateSchedulerList', { data, where });
export const deleteSchedule = (where) =>
  post('DeleteSchedulerList', { where });

// ---------------- Wall Mode ----------------
export const getWallPanels = (userId) =>
  post('GetWallPanelsByUserId', { UserId: userId });
export const addWallPanel = (payload) => post('AddWallPanel', payload);
export const updateWallPanel = (data, where) =>
  post('UpdateWallPanel', { data, where });
export const deleteWallPanel = (where) => post('DeleteWallPanel', { where });

export const addWallButton = (payload) => post('AddWallButton', payload);
export const updateWallButton = (data, where) =>
  post('UpdateWallButton', { data, where });
export const deleteWallButton = (where) => post('DeleteWallButton', { where });

export const addWallButtonAction = (payload) =>
  post('AddWallButtonAction', payload);
export const deleteWallButtonActionsByButtonId = (wallButtonId) =>
  post('DeleteWallButtonActionsByWallButtonId', { WallButtonId: wallButtonId });

export default api;
